import { useField } from 'formik';
import _ from 'lodash';
import {
	ComponentProps,
	KeyboardEvent,
	ReactNode,
	forwardRef,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { Input } from '../form/controls/Input';
import { CheckmarkIcon, ChevronDownIcon, CloseIcon, SearchIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { IconButton } from './button/IconButton';
import { Highlight } from './Highlight';
import { Popover } from './Popover';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Label } from './Text';

export type Option = {
	value?: string | number;
	title: string;
	description?: string;
	icon?: ReactNode;
	color?: string;
	context?: ReactNode;
	disabled?: boolean;
};

type Section = {
	title: string;
	options: (Option | undefined)[];
};

const Container = styled(Flex)<{
	disabled?: boolean;
	small?: boolean;
	$fit?: boolean;
}>`
	border-radius: ${tokens.radius.sm}px;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.default};
	box-shadow: ${tokens.shadow.button};
	box-sizing: border-box;
	transition: 0.1s ease-out all;
	height: ${(props) => (props.small ? 32 : 33)}px;
	padding: 0 ${tokens.padding.control}px;
	gap: ${tokens.gap.element}px;
	cursor: pointer;
	align-items: center;
	min-width: 0;
	width: ${(props) => (props.$fit ? 'fit-content' : '100%')};
	max-width: 100%;
	overflow: hidden;
	color: ${tokens.text.primary};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	font-family: ${tokens.typography.fontFamily};

	${(props) =>
		props.disabled &&
		`
		opacity: 0.5;
		pointer-events: none;
	`}

	&:hover,
	&[data-preview='hover'] {
		background: ${tokens.surface.hover};
		border-color: ${tokens.border.strong};
	}

	&:focus-visible {
		box-shadow:
			${tokens.shadow.button},
			0px 0px 0px 1px ${tokens.text.primary},
			0px 0px 0px 3px ${tokens.text.primary}33;
	}
`;

const TriggerLabel = styled.span<{ $placeholder?: boolean; $fit?: boolean }>`
	min-width: 0;
	flex: ${(props) => (props.$fit ? '0 1 auto' : '1 1 auto')};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	${(props) => props.$placeholder && `color: ${tokens.text.tertiary};`}
`;

const TriggerIcon = styled(Flex)<{ tint?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	height: 16px;
	width: 16px;

	> svg {
		display: block;
		width: 16px;
		height: 16px;

		${(props) =>
			props.tint !== false &&
			`
            path {
                fill: ${tokens.icon.primary};
            }
        `}
	}
`;

const Leading = styled(Flex)`
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
`;

export type SelectTriggerProps = {
	children?: ReactNode;
	leading?: ReactNode;
	leadingTint?: boolean;
	placeholder?: boolean;
	disabled?: boolean;
	small?: boolean;
	fit?: boolean;
	onClick?: () => void;
	onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
	role?: string;
	'aria-label'?: string;
	'aria-expanded'?: boolean;
	'data-preview'?: string;
};

const SelectTrigger = forwardRef<HTMLDivElement, SelectTriggerProps>(
	function SelectTrigger(props, ref) {
		const label =
			typeof props.children === 'string' || typeof props.children === 'number'
				? String(props.children)
				: undefined;
		const showLabel =
			props.children !== undefined && props.children !== null && props.children !== '';

		return (
			<Container
				ref={ref}
				small={props.small}
				disabled={props.disabled}
				$fit={props.fit}
				role={props.role ?? 'combobox'}
				aria-label={props['aria-label'] ?? label}
				aria-expanded={props['aria-expanded']}
				data-preview={props['data-preview']}
				onClick={props.onClick}
				onKeyDown={props.onKeyDown}
				tabIndex={0}
			>
				{props.leading &&
					(props.leadingTint === false ? (
						<Leading>{props.leading}</Leading>
					) : (
						<TriggerIcon tint={props.leadingTint}>{props.leading}</TriggerIcon>
					))}
				{showLabel ? (
					<TriggerLabel $placeholder={props.placeholder} $fit={props.fit} title={label}>
						{props.children}
					</TriggerLabel>
				) : null}
				<TriggerIcon>
					<ChevronDownIcon />
				</TriggerIcon>
			</Container>
		);
	},
);

export type SelectProps = ComponentProps<typeof Select>;

export function Select(
	props: ComponentProps<typeof SelectMenu> & {
		small?: boolean;
		fit?: boolean;
		selectedOption?: Option;
		placeholder?: string;
		leading?: ReactNode;
		fetch?: () => void;
		clearable?: boolean;
		autofocus?: boolean;
		autofocusMenu?: boolean;
		/** Stacking order for the menu popover. Use above DRAWER_Z_INDEX inside drawers. */
		zIndex?: number;
		role?: string;
		'aria-label'?: string;
		'data-preview'?: string;
	},
) {
	const [visible, setVisible] = useState(props.autofocus || false);

	const containerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const flatOptions = _.flatMap(
		props.options,
		(option) => _.get(option, 'options') ?? option,
	) as unknown as Option[];

	const option =
		props.selectedOption ?? flatOptions?.find((option) => option?.value === props.selectedValue);

	useEffect(() => {
		if (!visible) return;
		// Timeout to ensure the menu is mounted
		setTimeout(() => {
			menuRef.current?.focus({ preventScroll: true });
		}, 0);
		props.fetch?.();
	}, [visible]);

	return (
		<Flex
			alignItems="center"
			flexDirection="row"
			minWidth={0}
			width={props.fit ? undefined : '100%'}
		>
			<Box flexGrow={props.fit ? 0 : 1} minWidth={0}>
				<Popover.Target
					visible={visible}
					zIndex={props.zIndex}
					style={{ minWidth: 0, width: props.fit ? 'auto' : '100%' }}
					contents={
						<Select.Menu
							{...props}
							menuRef={menuRef}
							autofocus={props.autofocus || props.autofocusMenu}
							onChange={(value) => {
								props.onChange(value);
								setVisible(false);
								containerRef.current?.focus();
							}}
							onClose={() => {
								setVisible(false);
								containerRef.current?.focus();
							}}
						/>
					}
					onDismiss={() => setVisible(false)}
				>
					<SelectTrigger
						ref={containerRef}
						small={props.small}
						fit={props.fit}
						disabled={props.disabled}
						role={props.role ?? 'combobox'}
						aria-expanded={visible}
						aria-label={props['aria-label']}
						data-preview={props['data-preview']}
						leading={option?.icon ?? props.leading}
						leadingTint={props.tint}
						placeholder={!option}
						onClick={() => setVisible(true)}
						onKeyDown={(e) => {
							if (e.key === ' ') {
								e.preventDefault();
								setVisible(true);
							}
						}}
					>
						{option ? option.title : props.placeholder}
					</SelectTrigger>
				</Popover.Target>
			</Box>
			{props.clearable && option && (
				<IconButton
					ml={1}
					icon={<CloseIcon />}
					onClick={() => props.onChange(undefined)}
					type="secondary"
					small
				/>
			)}
		</Flex>
	);
}

function SelectSkeleton() {
	return <Skeleton.Box height={33} />;
}

Select.Skeleton = SelectSkeleton;

const Header = styled(Box)`
	border-bottom: 1px solid ${tokens.border.subtle};
`;

// Same emphasis as ContextMenu and AccountMenu: beige surface, primary text and icon.
const menuOptionEmphasis = css`
	background: ${tokens.surface.secondary};
	color: ${tokens.text.primary};

	&& svg {
		color: ${tokens.icon.primary};
	}

	&& svg path {
		fill: ${tokens.icon.primary};
	}
`;

const MenuOptionContainer = styled(Flex)<{
	hover?: boolean;
	disabled?: boolean;
	$checked?: boolean;
}>`
	border-radius: ${tokens.radius.sm}px;
	cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
	padding: ${tokens.padding.compact}px;
	align-items: center;
	gap: ${tokens.gap.element}px;
	width: 100%;
	box-sizing: border-box;
	color: ${tokens.text.secondary};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	font-family: ${tokens.typography.fontFamily};
	line-height: 20px;
	background: transparent;

	${(props) => props.$checked && menuOptionEmphasis}

	${(props) =>
		!props.disabled &&
		css`
			&:hover,
			&:active {
				${menuOptionEmphasis}
			}
		`}

	${(props) =>
		props.disabled &&
		css`
			opacity: 0.4;
		`}

	${(props) => props.hover && !props.disabled && menuOptionEmphasis}
`;

const Checkmark = styled(Box)`
	width: 16px;
	height: 16px;
	flex-grow: 0;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		display: block;
		width: 16px;
		height: 16px;

		path {
			fill: ${tokens.icon.primary};
		}
	}
`;

const IconContainer = styled(Flex)<{ tint?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	height: 20px;
	width: 20px;

	> svg {
		display: block;
		width: 20px;
		height: 20px;

		${(props) =>
			props.tint !== false &&
			`
            path {
                fill: ${tokens.icon.secondary};
            }
        `}
	}
`;

const Color = styled(Box)<{ $color: string }>`
	width: 12px;
	height: 12px;
	background: ${(props) => props.theme.colors[props.$color]};
	border-radius: 6px;
	flex-grow: 0;
	flex-shrink: 0;
`;

const Remaining = styled(Box)`
	color: ${tokens.text.tertiary};
	text-align: center;
	font-size: ${tokens.typography.sizes.small}px;
`;

const MenuSeparator = styled(Box)`
	background: ${tokens.border.subtle};
	height: 1px;
	width: 100%;
	flex-shrink: 0;
	margin: ${tokens.gap.element}px 0;
`;

const Context = styled(Box)`
	border-left: 1px solid ${tokens.border.subtle};
`;

function MenuOption(props: {
	tint?: boolean;
	scrollToHover?: boolean;
	focused?: boolean;
	hover?: boolean;
	checked?: boolean;
	color?: string;
	icon?: ReactNode;
	title: string;
	description?: string;
	query?: string;
	onMouseEnter: () => void;
	onSelect: () => void;
	disabled?: boolean;
}) {
	const optionRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (props.scrollToHover && props.hover) {
			optionRef.current?.scrollIntoView({
				block: 'nearest',
				behavior: 'smooth',
			});
		}
	}, [props.hover, props.scrollToHover]);

	return (
		<MenuOptionContainer
			ref={optionRef}
			role="option"
			aria-label={props.title}
			aria-selected={props.checked}
			hover={props.hover}
			$checked={props.checked}
			onClick={() => !props.disabled && props.onSelect()}
			onMouseEnter={() => !props.disabled && props.onMouseEnter()}
			disabled={props.disabled}
		>
			{props.checked && (
				<Checkmark>
					<CheckmarkIcon />
				</Checkmark>
			)}
			{props.icon && <IconContainer tint={props.tint}>{props.icon}</IconContainer>}
			{props.color && <Color $color={props.color} />}
			<Box>
				<Highlight query={props.query}>{props.title}</Highlight>
				{props.description && <small>{props.description}</small>}
			</Box>
		</MenuOptionContainer>
	);
}

const SelectMenuSection = styled(Box)`
	&:not(:last-child) {
		margin-bottom: 8px;
		padding-bottom: 4px;
		border-bottom: 1px solid ${tokens.border.subtle};
	}
`;

function SelectMenuSectionOrOption(props: {
	option: Option | Section | undefined;
	tint?: boolean;
	query?: string;
	hover?: string | number;
	scrollToHover?: boolean;
	onHover: (value?: string | number) => void;
	selectedValue?: string;
	onChange: (value?: string | number, option?: Option) => void;
}) {
	if (props.option === undefined) {
		return <MenuSeparator />;
	}

	if ('options' in props.option) {
		return (
			<SelectMenuSection>
				<Label my={1} mx={2}>
					{props.option.title}
				</Label>
				{props.option.options.map((option, index) => (
					<SelectMenuSectionOrOption
						key={index}
						{...props}
						option={option}
						onHover={(value) => props.onHover(value)}
					/>
				))}
			</SelectMenuSection>
		);
	}

	const option = props.option;

	return (
		<MenuOption
			key={option.value}
			scrollToHover={props.scrollToHover}
			hover={option.value === props.hover}
			checked={props.selectedValue === option.value}
			title={option.title}
			description={option.description}
			tint={props.tint}
			icon={option.icon}
			color={option.color}
			query={props.query}
			onMouseEnter={() => props.onHover(option.value)}
			onSelect={() => props.onChange(option.value, option)}
			disabled={option.disabled}
		/>
	);
}

function SelectMenu(props: {
	tint?: boolean;
	search?: boolean;
	query?: string;
	options?: (Option | undefined)[] | Section[];
	selectedValue?: string;
	onChange: (value?: string, option?: Option) => void;
	onQueryChange?: (query: string) => void;
	onClose?: () => void;
	disabled?: boolean;
	autofocus?: boolean;
	disableFilter?: boolean;
	menuRef?: React.RefObject<HTMLDivElement>;
	searchPlaceholder?: string;
}) {
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	const actualQuery = props.query ?? query;
	const inputRef = useRef<HTMLInputElement>(null);

	const flatOptions = _.flatMap(
		props.options,
		(option) => _.get(option, 'options') ?? option,
	) as unknown as Option[];

	const filterOptions = props.options?.flatMap((option) => {
		if (props.disableFilter) {
			return [option];
		}

		if (option === undefined) {
			return actualQuery.length ? [] : [option];
		}

		if ('options' in option) {
			const options = option.options.filter(
				(option) =>
					option === undefined || option.title.toLowerCase().includes(actualQuery.toLowerCase()),
			);
			return options.length ? [{ ...option, options }] : [];
		}

		if (option.title.toLowerCase().includes(actualQuery.toLowerCase())) {
			return [option];
		}

		return [];
	});

	const flatFilterOptions = _.flatMap(
		filterOptions,
		(option) => _.get(option, 'options') ?? option,
	);

	const [hoverState, setHoverState] = useState<
		| {
				value: string | number | undefined;
				action: 'hover' | 'focus';
		  }
		| undefined
	>(undefined);

	const anyHasContext = flatOptions?.some((option) => !!option?.context);
	const context =
		hoverState && flatOptions?.find((option) => option?.value === hoverState.value)?.context;

	// Support for dynamic options
	useEffect(() => {
		props.onQueryChange?.(query);
	}, [query, props]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			e.preventDefault();

			const currentFlatIndex = flatFilterOptions.findIndex(
				(opt) => opt?.value === hoverState?.value,
			);
			const newFlatIndex =
				(e.key === 'ArrowDown' ? currentFlatIndex + 1 : currentFlatIndex - 1) %
				flatFilterOptions.length;

			if (newFlatIndex < 0) {
				inputRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
				inputRef.current?.focus();
				return;
			}

			setHoverState({
				value: flatFilterOptions[newFlatIndex].value,
				action: 'focus',
			});
		}

		if (e.key === 'Enter') {
			e.preventDefault();
			const hoveredOption = flatFilterOptions.find((opt) => opt?.value === hoverState?.value);
			if (hoveredOption) {
				props.onChange(hoveredOption.value as string, hoveredOption);
			}
		}

		if (e.key === 'Escape') {
			e.preventDefault();
			props.onClose?.();
		}
	};

	return (
		<Flex
			height={anyHasContext ? 360 : undefined}
			maxHeight={360}
			style={{ position: 'relative' }}
			onKeyDown={handleKeyDown}
			tabIndex={-1}
			ref={props.menuRef}
			onFocus={() => props.autofocus && inputRef.current?.focus()}
		>
			<Box
				width={anyHasContext || props.search !== false ? 240 : undefined}
				minWidth={160}
				overflowY="auto"
			>
				{props.search !== false && (
					<Header p={2}>
						<Input
							inputRef={inputRef}
							small
							icon={<SearchIcon />}
							value={query}
							onChange={setQuery}
							autoFocus={props.autofocus}
							placeholder={props.searchPlaceholder ?? t('common.search')}
						/>
					</Header>
				)}
				<Box p="8px" role="listbox">
					{filterOptions?.map((option, index) => (
						<SelectMenuSectionOrOption
							key={index}
							option={option}
							onChange={props.onChange}
							selectedValue={props.selectedValue}
							hover={hoverState?.value}
							scrollToHover={hoverState?.action === 'focus'}
							onHover={(value) => setHoverState({ value, action: 'hover' })}
							query={query}
							tint={props.tint}
						/>
					)) ?? (
						<Flex p={2} justifyContent="center">
							<Spinner />
						</Flex>
					)}

					{props.options &&
						flatFilterOptions &&
						flatFilterOptions.length !== flatOptions.length && (
							<Remaining p={2}>
								{t('common.select.remaining', {
									count: flatOptions.length - flatFilterOptions.length,
								})}
							</Remaining>
						)}
				</Box>
			</Box>
			{context && (
				<Context width={240} p={2} maxHeight={360} overflowY="auto">
					{context}
				</Context>
			)}
		</Flex>
	);
}

Select.Trigger = SelectTrigger;
Select.Menu = SelectMenu;

function SelectFormik(
	props: Omit<ComponentProps<typeof Select>, 'selectedOption' | 'selectedValue' | 'onChange'> & {
		name: string;
	},
) {
	const [{ value }, , { setValue }] = useField(props.name);

	return <Select {...props} selectedValue={value} onChange={(value) => setValue(value)} />;
}

Select.Formik = SelectFormik;
